export const DOC_INTEL_POLL_MS = 2500;

export const UPLOAD_STATUS = {
  QUEUED: 'queued',
  EXTRACTING: 'extracting',
  REVIEW_PENDING: 'review_pending',
  APPROVED: 'approved',
  APPLIED: 'applied',
  FAILED: 'failed',
  DISCARDED: 'discarded'
};

export function uploadNeedsPolling(status) {
  return status === UPLOAD_STATUS.QUEUED || status === UPLOAD_STATUS.EXTRACTING;
}

export function getUploadId(upload) {
  return upload?.uploadId ?? upload?.id ?? null;
}

export function getReviewSessionId(upload) {
  return (
    upload?.reviewSessionId ??
    upload?.latestReview?.id ??
    null
  );
}

export function getPanelForUpload(upload, { mode = 'deal' } = {}) {
  if (!upload) return 'empty';
  switch (upload.status) {
    case UPLOAD_STATUS.QUEUED:
    case UPLOAD_STATUS.EXTRACTING:
      return 'processing';
    case UPLOAD_STATUS.REVIEW_PENDING:
      return 'review';
    case UPLOAD_STATUS.APPROVED:
      return mode === 'template' ? 'template' : 'mapping';
    case UPLOAD_STATUS.APPLIED:
      return mode === 'template' ? 'template' : 'success';
    case UPLOAD_STATUS.FAILED:
      return 'failed';
    default:
      return 'empty';
  }
}

export function getStatusBadge(status) {
  switch (status) {
    case UPLOAD_STATUS.QUEUED:
      return { label: 'Queued', className: 'bg-slate-100 text-slate-600' };
    case UPLOAD_STATUS.EXTRACTING:
      return { label: 'Reading…', className: 'bg-sky-50 text-sky-800' };
    case UPLOAD_STATUS.REVIEW_PENDING:
      return { label: 'Review', className: 'bg-amber-50 text-amber-800' };
    case UPLOAD_STATUS.APPROVED:
      return { label: 'Approved', className: 'bg-teal-50 text-teal-800' };
    case UPLOAD_STATUS.APPLIED:
      return { label: 'Applied', className: 'bg-emerald-50 text-emerald-800' };
    case UPLOAD_STATUS.FAILED:
      return { label: 'Failed', className: 'bg-red-50 text-red-700' };
    default:
      return { label: status || '—', className: 'bg-slate-100 text-slate-600' };
  }
}

/**
 * Parse API / Postgres timestamps correctly.
 * Values without a timezone (e.g. `2026-07-16T10:07:00.000`) are UTC from the server —
 * treating them as local (IST) makes times look ~5–6 hours off.
 */
export function parseApiDate(date) {
  if (!date) return null;
  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const raw = String(date).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?$/.test(raw)) {
    const parsed = new Date(`${raw.replace(' ', 'T')}Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * @param {string|Date|null|undefined} date
 * @param {number} [nowMs] — pass a ticking clock so relative labels stay fresh
 */
export function formatRelativeTime(date, nowMs = Date.now()) {
  const parsed = parseApiDate(date);
  if (!parsed) return '';

  const diffMs = nowMs - parsed.getTime();
  if (diffMs < 0) return 'Just now';

  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return parsed.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Absolute local time for version history / audit rows. */
export function formatAbsoluteTime(date) {
  const parsed = parseApiDate(date);
  if (!parsed) return '';
  return parsed.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Compact created/updated line for Studio lists.
 * Prefer showing both when they differ; always include absolute for clarity.
 */
export function formatCreatedUpdatedLine(createdAt, updatedAt, nowMs = Date.now()) {
  const createdAbs = formatAbsoluteTime(createdAt);
  const updatedAbs = formatAbsoluteTime(updatedAt);
  const createdRel = formatRelativeTime(createdAt, nowMs);
  const updatedRel = formatRelativeTime(updatedAt, nowMs);

  if (!createdAbs && !updatedAbs) return '';

  const createdPart = createdAbs
    ? `Created ${createdAbs}${createdRel ? ` (${createdRel})` : ''}`
    : '';
  const sameInstant =
    createdAt &&
    updatedAt &&
    Math.abs((parseApiDate(createdAt)?.getTime() || 0) - (parseApiDate(updatedAt)?.getTime() || 0)) <
      2000;

  if (!updatedAbs || sameInstant) {
    return createdPart || `Updated ${updatedAbs}${updatedRel ? ` (${updatedRel})` : ''}`;
  }

  return `${createdPart} · Updated ${updatedAbs}${updatedRel ? ` (${updatedRel})` : ''}`;
}

export function formatFileSize(bytes) {
  if (bytes == null || bytes === '') return null;
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function groupFieldsBySection(fields = []) {
  const groups = {};
  for (const field of fields) {
    const parts = (field.canonical_name || '').split('.');
    const section =
      parts.length >= 2 && /^\d+$/.test(parts[1])
        ? `${parts[0]}.${parts[1]}`
        : parts[0] || 'other';
    const label =
      section.startsWith('applicant.')
        ? section === 'applicant' || section === 'applicant.1'
          ? 'Primary applicant'
          : `Co-applicant ${section.split('.')[1]}`
        : section.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    if (!groups[label]) groups[label] = [];
    groups[label].push(field);
  }
  return groups;
}

export function confidenceTone(confidence) {
  const c = Number(confidence ?? 1);
  if (c < 0.75) return 'text-red-600';
  if (c < 0.85) return 'text-amber-700';
  return 'text-slate-500';
}

export const TARGET_TYPE_LABELS = {
  application_column: 'Application column',
  dynamic_field: 'Dynamic field',
  static: 'Static',
  custom_json: 'Custom JSON',
  computed: 'Computed (deal pricing)'
};

export const WORKFLOW_PHASES_CORE = [
  { id: 'processing', label: 'Upload' },
  { id: 'review', label: 'Review' },
  { id: 'mapping', label: 'Map' },
  { id: 'success', label: 'Apply' }
];

export const WORKFLOW_PHASE_TEMPLATE = { id: 'template', label: 'Template' };

/** @deprecated use getPhaseSteps */
export const WORKFLOW_PHASES = WORKFLOW_PHASES_CORE;

export const WORKFLOW_PHASES_TEMPLATE = [
  { id: 'processing', label: 'Upload' },
  { id: 'review', label: 'Review' },
  { id: 'template', label: 'Template' }
];

export function getActivePhaseIndex(panel, { includeTemplate = false, mode = 'deal' } = {}) {
  if (mode === 'template') {
    const map = { processing: 0, review: 1, template: 2, failed: 0 };
    return map[panel] ?? 0;
  }
  const map = includeTemplate
    ? { processing: 0, review: 1, mapping: 2, success: 3, template: 4, failed: 0 }
    : { processing: 0, review: 1, mapping: 2, success: 3, failed: 0 };
  return map[panel] ?? 0;
}

export function getPhaseSteps(panel, { includeTemplate = false, mode = 'deal' } = {}) {
  const phases =
    mode === 'template'
      ? WORKFLOW_PHASES_TEMPLATE
      : includeTemplate
        ? [...WORKFLOW_PHASES_CORE, WORKFLOW_PHASE_TEMPLATE]
        : WORKFLOW_PHASES_CORE;
  const activeIndex = getActivePhaseIndex(panel, { includeTemplate, mode });

  return phases.map((phase, index) => ({
    ...phase,
    state:
      panel === 'failed' && index === 0
        ? 'active'
        : index < activeIndex
          ? 'done'
          : index === activeIndex
            ? 'active'
            : 'pending'
  }));
}

export function extractPlaceholderNames(content = '') {
  const names = new Set();
  const re = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;
  let match = re.exec(content);
  while (match) {
    names.add(match[1]);
    match = re.exec(content);
  }
  return [...names];
}

/** Stable alphabetical order so chip panels do not reshuffle when HTML order changes. */
export function stablePlaceholderList(names = []) {
  return [...new Set((names || []).filter(Boolean))].sort((a, b) =>
    String(a).localeCompare(String(b))
  );
}

export function getPanelMeta(panel, upload, { mode = 'deal' } = {}) {
  const name = upload?.original_filename || 'Document';
  switch (panel) {
    case 'processing':
      return {
        title: upload?.status === 'extracting' ? 'Reading document' : 'Queued for extraction',
        subtitle: name,
        hint:
          mode === 'template'
            ? 'AI is reading your sample agreement to detect merge fields.'
            : 'AI is pulling text and fields — usually takes a few seconds.'
      };
    case 'review':
      return {
        title: 'Review extracted fields',
        subtitle: name,
        hint:
          mode === 'template'
            ? 'Confirm detected placeholders, then approve to build the template.'
            : 'Fix low-confidence values, then approve to unlock mapping.'
      };
    case 'mapping':
      return {
        title: 'Map to CRM',
        subtitle: name,
        hint: 'Confirm where each value lands on the deal before applying.'
      };
    case 'success':
      return {
        title: 'Applied to deal',
        subtitle: name,
        hint: 'Download a fresh agreement draft to confirm mapped KYC fields.'
      };
    case 'failed':
      return {
        title: 'Extraction failed',
        subtitle: name,
        hint: upload?.error_message || 'Try a clearer scan or a different file format.'
      };
    case 'template':
      return {
        title: mode === 'template' ? 'Agreement template' : 'Agreement template',
        subtitle: name,
        hint:
          mode === 'template'
            ? 'Use the rich editor to place merge fields, map them below, then promote for use on closed deals.'
            : 'Edit content. Placeholders auto-fill from the deal after apply.'
      };
    default:
      return {
        title: mode === 'template' ? 'Upload sample agreement' : 'Smart upload',
        subtitle: null,
        hint:
          mode === 'template'
            ? 'Upload a sample PDF or agreement to create a reusable template.'
            : null
      };
  }
}

export function humanizeFieldName(canonicalName) {
  return getFieldLabel(canonicalName);
}

/** Plain-English catalog — keep in sync with housingram-be fieldRegistry FIELD_LABEL_CATALOG. */
export const FIELD_LABELS = {
  'applicant.name': {
    label: 'Buyer full name',
    description: "Primary buyer's full legal name from the Application form"
  },
  'applicant.aadhar': {
    label: 'Buyer Aadhaar',
    description: "Primary buyer's 12-digit Aadhaar number"
  },
  'applicant.pan': {
    label: 'Buyer PAN',
    description: "Primary buyer's PAN card number"
  },
  'applicant.address': {
    label: 'Buyer address',
    description: "Primary buyer's postal / correspondence address"
  },
  'applicant.mobile': {
    label: 'Buyer mobile',
    description: "Primary buyer's phone number"
  },
  'applicant.email': {
    label: 'Buyer email',
    description: "Primary buyer's email address"
  },
  'applicant.dob': {
    label: 'Buyer date of birth',
    description: "Primary buyer's date of birth"
  },
  'applicant.2.name': {
    label: 'Co-buyer name',
    description: "Second applicant's full legal name"
  },
  'applicant.2.aadhar': { label: 'Co-buyer Aadhaar', description: "Second applicant's Aadhaar" },
  'applicant.2.pan': { label: 'Co-buyer PAN', description: "Second applicant's PAN" },
  'applicant.2.address': { label: 'Co-buyer address', description: "Second applicant's address" },
  'company.name': {
    label: 'Builder / company name',
    description: 'Promoter company name from Company settings'
  },
  'company.address': { label: 'Company address', description: 'Registered address of the builder' },
  'company.email': { label: 'Company email', description: 'Official company email' },
  'company.mobile': { label: 'Company phone', description: 'Official company phone' },
  'company.gst': { label: 'Company GSTIN', description: 'Builder GST registration number' },
  'company.pan': { label: 'Company PAN', description: 'Builder PAN' },
  'project.name': { label: 'Project name', description: 'Real-estate project name for this deal' },
  project_name: { label: 'Project name', description: 'Real-estate project name for this deal' },
  'unit.name': { label: 'Unit / apartment number', description: 'Allotted unit identifier (e.g. C-110)' },
  'unit.block': { label: 'Block / tower', description: 'Building block or tower name' },
  'unit.price.base': {
    label: 'Unit price (before tax)',
    description: 'Base sale price of the unit, excluding GST'
  },
  'unit.gst_percent': { label: 'GST %', description: 'GST percentage applied to the unit price' },
  'sale.gst_amount': {
    label: 'GST amount',
    description: 'Calculated as unit price × GST% — cannot be typed by hand'
  },
  'sale.total': {
    label: 'Total consideration',
    description: 'Unit price + GST — calculated automatically'
  },
  'sale.total.words': {
    label: 'Total in words',
    description: 'Total consideration written out in Indian rupees'
  },
  'sale.base.words': {
    label: 'Base price in words',
    description: 'Unit price (before tax) written out in words'
  },
  'table.pricing': {
    label: 'Pricing table',
    description: 'Auto-filled consideration / price breakdown table'
  },
  'land.name': { label: 'Land name', description: 'Project land parcel name' },
  'land.address': { label: 'Land address', description: 'Project land / site address' },
  building_rera_registration_no: {
    label: 'RERA registration number',
    description: 'Project RERA ID from building records'
  },
  accomodation_carpetarea: {
    label: 'Carpet area',
    description: 'Unit carpet area from accommodation master'
  },
  accomodation_builduparea: {
    label: 'Built-up area',
    description: 'Unit built-up area from accommodation master'
  },
  accomodation_name: {
    label: 'Accommodation type',
    description: 'Accommodation name from master'
  },
  'accommodation.tag': {
    label: 'Accommodation tag',
    description: 'Unit type tag (e.g. 1.5BHK)'
  },
  'accommodation.name': {
    label: 'Accommodation type',
    description: 'Accommodation name from master'
  }
};

/** Placeholders that always resolve from the deal / DFV without a saved mapping row. */
const KNOWN_DEAL_FIELD_PATHS = new Set([
  'building_rera_registration_no',
  'accomodation_carpetarea',
  'accomodation_builduparea',
  'accomodation_name',
  'accommodation.tag',
  'accommodation.name',
  'unit.name',
  'unit.block',
  'unit.type',
  'unit.typology',
  'unit.accommodation_type',
  'unit.carpet_area',
  'unit.built_up_area',
  'unit.plot_area',
  'unit.facing',
  'unit.undivided_land_share',
  'payment.booking_amount',
  'payment.booking_amount.words',
  'payment.cheque_number',
  'project.name',
  'project.city',
  'project_name',
  'company.name',
  'company.address',
  'company.email',
  'company.mobile',
  'company.gst',
  'company.pan',
  'unit.price.base',
  'unit.gst_percent',
  'sale.gst_amount',
  'sale.total',
  'sale.total.words',
  'sale.base.words',
  'table.pricing',
  'table.salient_features',
  'registration.rera_number',
  'registration.rera_date',
  'registration.rera_authority',
  'commencement_certificate.letter_number',
  'commencement_certificate.date',
  'commencement_certificate.issuing_authority',
  'layout_approval.letter_number',
  'layout_approval.date',
  'layout_approval.issuing_authority',
  'apartment_ownership_act.name',
  'possession.date',
  'execution.city',
  'execution.date',
  'execution.sub_registrar_office',
  'payment.payable_city',
  'payment.penalty_interest_rate',
  'promoter.signatory_name',
  'promoter.signatory_address'
]);

function fallbackHumanize(canonicalName) {
  const raw = (canonicalName || '').trim();
  if (!raw) return '';
  const parts = raw.split('.').filter(Boolean);
  if (parts.length <= 1) {
    return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return parts
    .map((part) => part.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(' ');
}

/** Prefer catalog label; fall back to humanized key. Technical key stays for tooltips. */
export function getFieldLabel(canonicalName, registryLabels) {
  const key = (canonicalName || '').trim().toLowerCase();
  if (!key) return '';
  const fromApi = registryLabels?.[key]?.label;
  if (fromApi) return fromApi;
  if (FIELD_LABELS[key]?.label) return FIELD_LABELS[key].label;

  const match = key.match(/^applicant(?:\.(\d+))?\.([a-z_]+)$/);
  if (match) {
    const n = match[1] ? parseInt(match[1], 10) : 1;
    const who = n <= 1 ? 'Buyer' : `Co-buyer ${n}`;
    const col = match[2].replace(/_/g, ' ');
    return `${who} ${col}`.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  if (key.startsWith('table.')) {
    return `${fallbackHumanize(key.slice(6))} table`;
  }
  return fallbackHumanize(canonicalName);
}

export function getFieldDescription(canonicalName, registryLabels) {
  const key = (canonicalName || '').trim().toLowerCase();
  if (!key) return '';
  return (
    registryLabels?.[key]?.description ||
    FIELD_LABELS[key]?.description ||
    ''
  );
}

/** Studio mapping section groups. */
export function getFieldGroup(canonicalName) {
  const key = (canonicalName || '').trim().toLowerCase();
  if (key.startsWith('applicant')) return 'About the Buyer';
  if (key.startsWith('unit') || key.startsWith('table.')) return 'About the Unit';
  if (key.startsWith('sale') || key.includes('price') || key.includes('gst')) return 'Pricing';
  if (key.startsWith('company')) return 'Company';
  if (key.startsWith('project') || key.startsWith('land') || key.includes('rera')) {
    return 'Project & land';
  }
  if (key.includes('date') || key === 'date') return 'Dates';
  return 'Other';
}

export function groupPlaceholdersBySection(placeholders = []) {
  const order = [
    'About the Buyer',
    'About the Unit',
    'Pricing',
    'Company',
    'Project & land',
    'Dates',
    'Other'
  ];
  const groups = {};
  for (const name of placeholders) {
    const section = getFieldGroup(name);
    if (!groups[section]) groups[section] = [];
    groups[section].push(name);
  }
  return order
    .filter((section) => groups[section]?.length)
    .map((section) => ({ section, placeholders: groups[section] }));
}

/** Visual status for Studio field chips — what a normal user needs to know. */
export const FIELD_STATUS = {
  from_deal: {
    id: 'from_deal',
    label: 'From deal',
    hint: 'Filled automatically from the closed deal',
    chipClass: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    chipStyle: 'background:#d1fae5;color:#065f46;border:1px solid #6ee7b7;',
    legendDot: 'bg-emerald-500'
  },
  needs_attention: {
    id: 'needs_attention',
    label: 'Needs attention',
    hint: 'Move these first — to You fill in (then type value there) or From deal',
    chipClass: 'bg-amber-100 text-amber-950 border-amber-300',
    chipStyle: 'background:#fef3c7;color:#92400e;border:1px solid #fcd34d;',
    legendDot: 'bg-amber-500'
  },
  user_fill: {
    id: 'user_fill',
    label: 'You fill in',
    hint: 'Moved here · type the fixed value on the field, then Save value',
    chipClass: 'bg-sky-100 text-sky-950 border-sky-300',
    chipStyle: 'background:#e0f2fe;color:#0c4a6e;border:1px solid #7dd3fc;',
    legendDot: 'bg-sky-500'
  },
  table: {
    id: 'table',
    label: 'Auto table',
    hint: 'Pricing / schedule table filled at PDF time',
    chipClass: 'bg-teal-50 text-teal-900 border-teal-300 border-dashed',
    chipStyle:
      'background:#f0fdfa;color:#115e59;border:1px dashed #14b8a6;',
    legendDot: 'bg-teal-600'
  }
};

/**
 * Classify a placeholder for the Studio UI.
 * @returns {'from_deal'|'needs_attention'|'user_fill'|'table'}
 */
export function classifyFieldStatus(placeholderName, fieldMappings = [], sampleLeaks = []) {
  const name = (placeholderName || '').trim();
  if (!name) return 'needs_attention';
  if (name.startsWith('table.')) return 'table';

  const mapping = (fieldMappings || []).find(
    (m) => (m.placeholder_name || '').toLowerCase() === name.toLowerCase()
  );
  // Explicit mappings win over sample-leak flags (user already set a fixed value / CRM path).
  if (mapping) {
    if (
      mapping.target_type === 'static' ||
      (mapping.static_value && String(mapping.static_value).trim())
    ) {
      return 'user_fill';
    }
    if (mapping.target_type === 'computed') return 'from_deal';
    if (mapping.target_path || mapping.target_type) return 'from_deal';
  }

  const leak = (sampleLeaks || []).find(
    (l) => (l.canonical_name || '').toLowerCase() === name.toLowerCase()
  );
  if (leak) return 'needs_attention';

  if (!mapping) {
    // Known deal/DFV keys (e.g. from salient table) are live even before Save maps them
    if (KNOWN_DEAL_FIELD_PATHS.has(name.toLowerCase())) return 'from_deal';
    if (/^applicant(?:\.\d+)?\./i.test(name)) return 'from_deal';
    return 'needs_attention';
  }
  return 'needs_attention';
}

/** Bucket order for Studio “move field here” panels. */
export const FIELD_STATUS_ORDER = [
  'needs_attention',
  'from_deal',
  'user_fill',
  'table'
];

/**
 * Group placeholders by From deal / Needs attention / You fill in / Auto table.
 */
export function groupPlaceholdersByStatus(
  placeholders = [],
  fieldMappings = [],
  sampleLeaks = []
) {
  const groups = {
    needs_attention: [],
    from_deal: [],
    user_fill: [],
    table: []
  };
  for (const name of placeholders) {
    const status = classifyFieldStatus(name, fieldMappings, sampleLeaks);
    const key = groups[status] ? status : 'needs_attention';
    groups[key].push(name);
  }
  // Always return every bucket so empty ones stay valid drop / Move-to targets.
  return FIELD_STATUS_ORDER.map((id) => ({
    status: id,
    ...FIELD_STATUS[id],
    placeholders: groups[id]
  }));
}

export const DOC_INTEL_UPLOAD_ACCEPT =
  '.pdf,.docx,.jpg,.jpeg,.png,.webp,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*,text/plain';

export function getFileVisual(name, mimeType) {
  const ext = (name?.split('.').pop() || '').toLowerCase();
  const mime = (mimeType || '').toLowerCase();

  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) || mime.startsWith('image/')) {
    return { tone: 'violet', label: 'IMG' };
  }
  if (['pdf'].includes(ext) || mime === 'application/pdf') {
    return { tone: 'rose', label: 'PDF' };
  }
  if (
    ['docx'].includes(ext) ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return { tone: 'sky', label: 'DOCX' };
  }
  if (['txt'].includes(ext) || mime.startsWith('text/')) {
    return { tone: 'sky', label: 'TXT' };
  }
  return { tone: 'slate', label: 'DOC' };
}

const FILE_TONE_CLASSES = {
  violet: 'bg-violet-50 text-violet-700 ring-violet-100',
  rose: 'bg-rose-50 text-rose-700 ring-rose-100',
  sky: 'bg-sky-50 text-sky-700 ring-sky-100',
  slate: 'bg-slate-100 text-slate-600 ring-slate-200/80'
};

export function getFileVisualClasses(name, mimeType) {
  const { tone, label } = getFileVisual(name, mimeType);
  return { className: FILE_TONE_CLASSES[tone] || FILE_TONE_CLASSES.slate, label };
}

export function isPreviewableImage(mimeType, filename) {
  const mime = (mimeType || '').toLowerCase();
  const ext = (filename?.split('.').pop() || '').toLowerCase();
  return mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext);
}
